import React, { useState, useRef, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import Calendar from 'react-calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from 'utils/cn';
import RequiredMark from 'components/common/RequiredMark';

interface CustomCalendarProps {
  name?: string;
  value: string | Date | null;
  onChange: (date: Date) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  variant?: 'default' | 'compact' | 'large';
  theme?: 'light' | 'dark';
  className?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  showNeighboringMonth?: boolean;
  showNavigation?: boolean;
  calendarClassName?: string;
  dropdownPosition?: 'bottom' | 'top';
  dropdownWidth?: number | string;
  minDropdownWidth?: number;
  required?: boolean;
  textClassName?: string;
  labelClassName?: string;
  // Generic hooks for a caller that needs to grey out specific dates (e.g.
  // non-working days) or react to month navigation — kept caller-agnostic
  // so this shared component has no leave-specific logic of its own.
  tileDisabled?: (date: Date) => boolean;
  onActiveMonthChange?: (activeStartDate: Date) => void;
  // Same caller-agnostic spirit as tileDisabled — lets a caller mark
  // specific dates (extra class name / extra content, e.g. a tooltip
  // marker) without this shared component knowing what that means.
  tileClassName?: (date: Date) => string | undefined;
  tileContent?: (date: Date) => React.ReactNode;
  // True while the caller is still fetching data (e.g. which dates are
  // selectable) for the month currently shown. Dims the grid and blocks
  // clicks so a tile can't briefly read as disabled/enabled before its real
  // data arrives — purely a loading affordance, this component still has no
  // idea what the data means.
  isLoadingMonth?: boolean;
  // Fired when the (disabled) field itself is clicked — e.g. so a caller can
  // toast a reason ("Select a leave type first") for users who click through
  // without reading the placeholder. No-op when the field isn't disabled.
  onDisabledClick?: () => void;
  // Keeps the picker inside one year: clicking the "October 2026" label opens
  // that year's twelve months to jump to, but goes no further — no decade
  // view, and no « » year-jump buttons. Pair with minDate/maxDate set to that
  // year's 1 Jan / 31 Dec; they also disable the year-to-year arrows in the
  // months view and stop < > at January and December.
  lockYear?: boolean;
  // Hover tooltip for a < > arrow while minDate/maxDate keep it disabled —
  // e.g. why a year-locked picker stops at January and December.
  navigationDisabledMessage?: string;
  // How the picked date reads in the field. Defaults to US "MM/DD/YYYY";
  // a caller whose design reads day-first passes its own.
  formatDisplayValue?: (date: Date) => string;
  // The month the picker opens on while nothing is picked. Defaults to
  // today's — a caller showing another year passes a date in it.
  defaultViewDate?: Date;
}

type CalendarView = 'month' | 'year' | 'decade' | 'century';

// Start of the period `n` steps after the one starting at `start`, per view.
const shiftPeriod = (view: CalendarView, start: Date, n: number): Date => {
  const y = start.getFullYear();
  if (view === 'month') return new Date(y, start.getMonth() + n, 1);
  const years = view === 'year' ? 1 : view === 'decade' ? 10 : 100;
  return new Date(y + years * n, 0, 1);
};

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  name,
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
  variant = 'default',
  theme = 'dark',
  className = '',
  disabled = false,
  error = false,
  errorMessage,
  label,
  showNeighboringMonth = true,
  showNavigation = true,
  calendarClassName = '',
  dropdownPosition = 'bottom',
  dropdownWidth,
  minDropdownWidth = 280,
  required = false,
  textClassName = '',
  labelClassName = '',
  tileDisabled,
  onActiveMonthChange,
  tileClassName,
  tileContent,
  isLoadingMonth = false,
  onDisabledClick,
  lockYear = false,
  navigationDisabledMessage,
  formatDisplayValue,
  defaultViewDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // The view and period react-calendar is showing, mirrored from its change
  // callbacks so the arrows' disabled state is known here. Null until the
  // first navigation after opening — the calendar remounts on every open.
  const [navState, setNavState] = useState<{
    view: CalendarView;
    activeStartDate: Date;
  } | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    // Undefined whenever the whole grid fits — the dropdown is then exactly
    // its content's height, with nothing to scroll.
    maxHeight?: number;
    placeAbove: boolean;
  }>({ top: 0, left: 0, width: 0, placeAbove: false });
  const buttonRef = useRef<HTMLDivElement>(null);
  const calendarId = useRef(
    `calendar-${Math.random().toString(36).substr(2, 9)}`,
  );

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const edgeMargin = 8;
      const spaceBelow = window.innerHeight - rect.bottom - edgeMargin;
      const spaceAbove = rect.top - edgeMargin;

      // The grid's real height, measured rather than assumed — a fixed guess
      // either clipped it (and forced a scrollbar) or picked a side it
      // didn't fit. scrollHeight is the full content even while capped, and
      // ignores the open animation's scale. 360 only before first paint.
      const dropdown = document.getElementById(calendarId.current);
      const contentHeight = dropdown?.scrollHeight || 360;

      // Below unless it doesn't fit there and above has more room.
      const shouldShowAbove =
        dropdownPosition === 'top' ||
        (dropdownPosition === 'bottom' && spaceBelow < contentHeight && spaceAbove > spaceBelow);

      // Capped only when neither side can hold the whole grid — the modal or
      // page behind is scroll-locked, so a clipped grid would be unreachable.
      // Even then no scrollbar is drawn (see no-scrollbar below).
      const availableHeight = shouldShowAbove ? spaceAbove : spaceBelow;
      const calculatedMaxHeight =
        contentHeight > availableHeight ? Math.max(availableHeight, 220) : undefined;

      let calculatedWidth: number;

      if (dropdownWidth) {
        calculatedWidth =
          typeof dropdownWidth === 'string'
            ? parseInt(dropdownWidth.replace('px', ''))
            : dropdownWidth;
      } else {
        calculatedWidth = Math.max(rect.width, minDropdownWidth);
      }

      let calculatedLeft = rect.left + window.scrollX;

      // Ensure dropdown doesn't render off the right edge of the screen
      if (rect.left + calculatedWidth > window.innerWidth) {
        // Align right edge of dropdown with right edge of the button
        calculatedLeft = rect.right + window.scrollX - calculatedWidth;
        // If it's still negative, just attach to the left edge of screen
        if (calculatedLeft < 0) calculatedLeft = 0;
      }

      setPosition({
        // Above, the dropdown's *bottom* is pinned 4px over the field (the
        // -translate-y-full below lifts it by its own height), so it sits
        // flush whatever its height — 5-week month, 6-week month or months view.
        top: shouldShowAbove
          ? rect.top + window.scrollY - 4
          : rect.bottom + window.scrollY + 4,
        left: calculatedLeft,
        width: calculatedWidth,
        maxHeight: calculatedMaxHeight,
        placeAbove: shouldShowAbove,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleResize = () => updatePosition();
      const handleScroll = () => updatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, dropdownWidth, minDropdownWidth]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        const dropdown = document.getElementById(calendarId.current);
        if (dropdown && !dropdown.contains(event.target as Node)) {
          // Simply close the calendar when clicking outside
          // Removed the filter and calendar dropdown checks
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      // Use capture phase to handle the event before other handlers
      document.addEventListener('mousedown', handleClickOutside, true);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside, true);
    }
  }, [isOpen]);

  const getCalendarClasses = () => {
    let classes = 'custom-calendar-dropdown';

    if (variant !== 'default') {
      classes += ` calendar-${variant}`;
    }

    if (theme !== 'light') {
      classes += ` calendar-${theme}`;
    }

    if (calendarClassName) {
      classes += ` ${calendarClassName}`;
    }

    return classes;
  };

  // const getDisplayValue = () => {
  //   if (!value) return placeholder;

  //   const date = value instanceof Date ? value : new Date(value);
  //   return date.toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: '2-digit',
  //     day: '2-digit',
  //   });
  // };

  const getDisplayValue = () => {
    if (!value) return placeholder;

    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      date = new Date(`${value}T00:00:00`); // force ISO parsing for YYYY-MM-DD
    } else {
      date = new Date(value);
    }

    // Fallback if parsing fails to avoid "Invalid Date"
    if (isNaN(date.getTime())) return String(value);

    if (formatDisplayValue) return formatDisplayValue(date);

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleCalendarChange = (calendarValue: any) => {
    if (calendarValue instanceof Date) {
      onChange(calendarValue);
      setIsOpen(false);
    } else if (
      Array.isArray(calendarValue) &&
      calendarValue[0] instanceof Date
    ) {
      onChange(calendarValue[0]);
      setIsOpen(false);
    }
  };

  const calendarValue = value
    ? value instanceof Date
      ? value
      : typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(`${value}T00:00:00`)
        : new Date(value)
    : new Date();

  // Only steers where an empty picker opens; a picked date always wins.
  const openingMonth =
    !value && defaultViewDate ? shiftPeriod('month', defaultViewDate, 0) : undefined;

  // Same rules react-calendar uses: it opens on the value's month clamped
  // into [minDate, maxDate], disables < when the previous period ends on or
  // before minDate, and > when the next one starts after maxDate.
  const getNavDisabled = () => {
    let nav = navState;
    if (!nav) {
      let start = openingMonth ?? calendarValue;
      if (minDate && start < minDate) start = minDate;
      if (maxDate && start > maxDate) start = maxDate;
      nav = { view: 'month', activeStartDate: shiftPeriod('month', start, 0) };
    }
    const previousEnd = new Date(nav.activeStartDate.getTime() - 1);
    return {
      prev: !!minDate && minDate >= previousEnd,
      next: !!maxDate && maxDate < shiftPeriod(nav.view, nav.activeStartDate, 1),
    };
  };

  // Shown by CSS (:disabled:hover in index.css) rather than a native title,
  // which only appears after the browser's hover delay, or HoverTooltip: a
  // disabled <button> never fires the mouse events a JS tooltip needs, but
  // :hover still matches it — so this appears the instant it's pointed at.
  const navArrowLabel = (glyph: string, isDisabled: boolean) => (
    <>
      {glyph}
      {isDisabled && (
        <span role="tooltip" className="calendar-nav-tooltip">
          {navigationDisabledMessage}
        </span>
      )}
    </>
  );
  const navDisabled = navigationDisabledMessage ? getNavDisabled() : null;

  const getInputClasses = () => {
    let classes = `flex justify-between items-center border rounded-[8px] px-4 h-[46px] cursor-pointer transition-colors ${className}`;

    if (disabled) {
      // No pointer-events-none here (unlike a plain disabled control) — a
      // click still needs to reach onDisabledClick below so a caller can
      // explain why the field is inactive.
      classes += ' bg-[#1E2230] cursor-not-allowed opacity-50 border-[#2A2F3D]';
    } else if (theme === 'dark') {
      classes += ' bg-[#1E2230] text-white hover:bg-[#252a3a]';
    } else {
      classes += ' bg-white hover:bg-gray-50';
    }

    if (error) {
      classes += ' border-red-500';
    } else if (theme === 'dark') {
      classes += ' border-[#2A2F3D] hover:border-[#3a4050]';
    } else {
      classes += ' border-gray-300 hover:border-gray-400';
    }

    return classes;
  };

  const calendarContent = (
    <Transition
      show={isOpen}
      as={Fragment}
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <div
        id={calendarId.current}
        className={`absolute z-[10000] bg-white rounded-lg shadow-lg border border-gray-200 overflow-y-auto overflow-x-hidden no-scrollbar ${
          theme === 'dark' ? 'bg-gray-800 border-gray-600' : ''
        } ${position.placeAbove ? '-translate-y-full' : ''}`}
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          maxHeight: position.maxHeight,
        }}
        data-calendar="true"
      >
        <div className="relative">
          {isLoadingMonth && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-[1px] cursor-wait">
              <div className="w-5 h-5 rounded-full border-2 border-t-transparent border-gray-300 animate-spin" />
            </div>
          )}
          <Calendar
            onChange={handleCalendarChange}
            value={calendarValue}
            // Read on mount only; the calendar remounts on every open.
            defaultActiveStartDate={openingMonth}
            minDate={minDate}
            maxDate={maxDate}
            {...(lockYear && {
              minDetail: 'year' as const,
              prev2Label: null,
              next2Label: null,
            })}
            {...(navDisabled && {
              prevLabel: navArrowLabel('‹', navDisabled.prev),
              nextLabel: navArrowLabel('›', navDisabled.next),
              onViewChange: ({ activeStartDate, view }) =>
                activeStartDate && setNavState({ view, activeStartDate }),
            })}
            showNeighboringMonth={showNeighboringMonth}
            showNavigation={showNavigation}
            className={getCalendarClasses()}
            tileDisabled={
              tileDisabled
                ? ({ date, view }) => view === 'month' && tileDisabled(date)
                : undefined
            }
            onActiveStartDateChange={
              onActiveMonthChange || navigationDisabledMessage
                ? ({ activeStartDate, view }) => {
                    if (!activeStartDate) return;
                    if (navigationDisabledMessage) setNavState({ view, activeStartDate });
                    onActiveMonthChange?.(activeStartDate);
                  }
                : undefined
            }
            tileClassName={
              tileClassName
                ? ({ date, view }) =>
                    view === 'month' ? tileClassName(date) : undefined
                : undefined
            }
            tileContent={
              tileContent
                ? ({ date, view }) =>
                    view === 'month' ? tileContent(date) : null
                : undefined
            }
          />
        </div>
      </div>
    </Transition>
  );

  return (
    <div className="w-full relative" data-field={name} id={name}>
      {label && (
        <label
          className={cn(
            `block text-[14px] font-normal mb-[11px] leading-[100%] ${
              theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-700'
            }`,
            labelClassName,
          )}
        >
          {label} {required && <RequiredMark />}
        </label>
      )}

      <div
        ref={buttonRef}
        data-field={name}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (disabled) return onDisabledClick?.();
          // A fresh open starts back on the value's month.
          if (!isOpen) setNavState(null);
          setIsOpen(!isOpen);
        }}
        className={getInputClasses()}
      >
        <span
          className={cn(
            `${
              value
                ? theme === 'dark'
                  ? 'text-gray-100 text-[16px]'
                  : 'text-gray-900 text-[16px]'
                : theme === 'dark'
                  ? 'text-neutral-medium text-[14px]'
                  : 'text-gray-500 text-[16px]'
            }`,
            textClassName,
          )}
        >
          {getDisplayValue()}
        </span>
        <CalendarIcon
          className={`w-4 h-4 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
          }`}
        />
      </div>

      {error && errorMessage && (
        <p className="text-sm text-red-500 mt-1">{errorMessage}</p>
      )}

      {typeof document !== 'undefined' &&
        createPortal(calendarContent, document.body)}
    </div>
  );
};

export default CustomCalendar;
